import { memo } from 'react'
import { Handle, Position, type Node } from '@xyflow/react'
import { Bot, AlertCircle, CheckCircle, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AgentNodeConfig } from './AgentNode'

export type AgentNodeStatus = 'idle' | 'configured' | 'executing' | 'success' | 'error'

export interface AgentCanvasNodeData extends AgentNodeConfig {
  status?: AgentNodeStatus
  errorMessage?: string
  skillName?: string
  onDelete?: () => void
  [key: string]: unknown
}

export type AgentCanvasNode = Node<AgentCanvasNodeData, 'agent'>

const statusIcons: Record<AgentNodeStatus, React.ReactNode> = {
  idle: null,
  configured: <CheckCircle className="h-3 w-3 text-emerald-500" />,
  executing: <Loader2 className="h-3 w-3 text-purple-500 animate-spin" />,
  success: <CheckCircle className="h-3 w-3 text-emerald-500" />,
  error: <AlertCircle className="h-3 w-3 text-red-500" />,
}

interface AgentCanvasNodeProps {
  data: AgentCanvasNodeData
  selected?: boolean
}

function AgentCanvasNodeComponent({ data, selected }: AgentCanvasNodeProps) {
  const status: AgentNodeStatus = data.status || (data.agentId ? 'configured' : 'idle')

  return (
    <div
      className={cn(
        'group relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 min-w-[200px] max-w-[240px] overflow-hidden',
        'border border-gray-200 dark:border-gray-700',
        selected && 'ring-2 ring-purple-500 ring-offset-2 dark:ring-offset-gray-900 border-purple-500'
      )}
    >
      {/* Left color accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500" />

      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
      />

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          data.onDelete?.()
        }}
        className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-all"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      {/* Node Content */}
      <div className="pl-4 pr-3 py-3">
        {/* Header */}
        <div className="flex items-start gap-2.5">
          <div className="relative flex-shrink-0 mt-0.5">
            <div className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/40">
              <Bot className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            {/* Status indicator */}
            {statusIcons[status] && (
              <div className="absolute -bottom-0.5 -right-0.5 p-0.5 bg-white dark:bg-gray-800 rounded-full">
                {statusIcons[status]}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 pr-4">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.agentName || (data.label as string) || 'AI Agent'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {data.skillName || (data.agentId ? 'Agent configured' : 'Click to configure')}
            </p>
          </div>
        </div>

        {/* Error message */}
        {status === 'error' && data.errorMessage && (
          <div className="mt-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20">
            <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">
              {data.errorMessage}
            </p>
          </div>
        )}

        {/* Output variable indicator */}
        {data.outputVariable && (
          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-400">
              <span className="font-mono text-purple-600 dark:text-purple-400">{`{{${data.outputVariable}}}`}</span>
            </p>
          </div>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5"
      />
    </div>
  )
}

export default memo(AgentCanvasNodeComponent)
